import { HttpErrorResponse } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, HostListener, Input, OnChanges, OnDestroy
} from "@angular/core";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzTagModule } from "ng-zorro-antd/tag";
import { auditTime, filter, map, merge, of, Subscription, switchMap } from "rxjs";
import { AssessmentTaskFormLaunchService } from "../../../services/assessment_task_form_launch.service";
import { AssessmentTaskLaunchService } from "../../../services/assessment_task_launch.service";
import { AssessmentTaskInvalidationService } from "../../../services/assessment_task_invalidation.service";
import type { AssessmentTaskInvalidation } from "../../../services/assessment_task_invalidation.service";
import { AssessmentTasksService } from "../../../services/assessment_tasks.service";
import { AssessmentWorkItemsService } from "../../../services/assessment_work_items.service";
import { workItemIdentity, workItemStatusPresentation } from "../../../services/assessment_work_items.types";
import type { AssessmentWorkItem, AssessmentWorkItemPage } from "../../../services/assessment_work_items.types";
import { assessmentTaskSessionIdentity } from "../../../services/assessment_task_session.util";
import type { AssessmentTaskRequirementRef } from "../../../services/assessment_tasks.types";
import { EvidenceRequirementsLaunchService } from "../../../services/evidence_requirements_launch.service";
import { EvidenceRequirementInvalidationService } from "../../../services/evidence_requirement_invalidation.service";
import type { EvidenceRequirementInvalidation } from "../../../services/evidence_requirement_invalidation.service";
import { LoginService } from "../../../services/login.service";
import { ProductLicenseSummary, ProductLicensesService } from "../../../services/product_licenses.service";
import { assessmentTaskDueState } from "../../../utils/assessment_task_date.util";
import { evidenceRequirementDueState } from "../../../utils/evidence_requirement_due_date.util";
import {
    canShowTaskAssignmentsFooter, evidenceChangeAffectsFooter, footerWorkItemFilters, FOOTER_PAGE_SIZE,
    scopeFooterWorkItems, validFooterRequirementKey
} from "./task_assignments_footer_policy";

const CREATION_OPTION_PAGE_SIZE = 100;

const positiveId = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

@Component({
    selector: "td[appTaskAssignmentsFooter]",
    standalone: true,
    imports: [CommonModule, NzButtonModule, NzIconModule, NzPaginationModule, NzTagModule],
    templateUrl: "./task_assignments_footer.component.html",
    styleUrls: ["./task_assignments_footer.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskAssignmentsFooterComponent implements OnChanges, OnDestroy {
    private static nextInstanceId = 0;
    readonly componentId = ++TaskAssignmentsFooterComponent.nextInstanceId;

    @Input() productLicenseId: number | null = null;
    @Input() sectionKey = "";
    @Input() requirementKey = "";
    @Input() scope: "requirement" | "assessment" = "requirement";

    @HostBinding("class") readonly hostClass = "task-assignments";

    @HostBinding("style.display")
    protected get hostDisplay(): string | null {
        return this.isVisible || this.isLoading || this.loadError ? null : "none";
    }

    protected workItems: AssessmentWorkItem[] = [];
    protected total = 0;
    protected pageIndex = 1;
    protected readonly pageSize = FOOTER_PAGE_SIZE;
    protected readonly statusPresentation = workItemStatusPresentation;
    protected readonly dueState = assessmentTaskDueState;
    protected readonly evidenceDueState = evidenceRequirementDueState;
    protected isLoading = false;
    protected isVisible = false;
    protected canAddTask = false;
    protected isCheckingCreation = false;
    protected loadError: string | null = null;
    protected creationError: string | null = null;
    protected isExpanded = false;

    private sessionIdentity: string | null = null;
    private sessionToken: string | null = null;
    private selectionKey: string | null = null;
    private requestVersion = 0;
    private pageRequest: Subscription | null = null;
    private creationRequest: Subscription | null = null;
    private readonly sessionSubscription: Subscription;
    private readonly invalidationSubscription: Subscription;

    constructor(
        private readonly taskService: AssessmentTasksService,
        private readonly workItemService: AssessmentWorkItemsService,
        private readonly invalidations: AssessmentTaskInvalidationService,
        private readonly evidenceInvalidations: EvidenceRequirementInvalidationService,
        private readonly taskBrowser: AssessmentTaskLaunchService,
        private readonly taskForm: AssessmentTaskFormLaunchService,
        private readonly evidenceLaunch: EvidenceRequirementsLaunchService,
        private readonly licenses: ProductLicensesService,
        private readonly login: LoginService,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.sessionSubscription = this.login.getUserUpdates().subscribe((user) => {
            const identity = assessmentTaskSessionIdentity(user);
            const token = this.login.getToken();
            if (identity === this.sessionIdentity && token === this.sessionToken) return;
            this.sessionIdentity = identity;
            this.sessionToken = token;
            this.pageIndex = 1;
            this.isExpanded = false;
            this.load();
        });
        this.invalidationSubscription = merge(
            this.invalidations.watchInvalidations().pipe(
                filter((event) => this.shouldRefreshForInvalidation(event))
            ),
            this.evidenceInvalidations.watchChanges().pipe(
                filter((change) => this.shouldRefreshForEvidenceInvalidation(change))
            )
        ).pipe(
            map(() => ({
                selectionKey: this.selectionKey,
                sessionIdentity: this.sessionIdentity,
                accountId: this.login.getCurrentAccountId(),
                token: this.login.getToken()
            })),
            auditTime(0)
        ).subscribe(({ selectionKey, sessionIdentity, accountId, token }) => {
            if (selectionKey === this.selectionKey && sessionIdentity === this.sessionIdentity &&
                accountId === this.login.getCurrentAccountId() && token === this.login.getToken()) this.load();
        });
    }

    ngOnChanges(): void {
        const requirement = this.requirement;
        const key = positiveId(this.productLicenseId) &&
            (this.scope === "assessment" || requirement)
            ? JSON.stringify([this.scope, this.productLicenseId, requirement?.sectionKey, requirement?.requirementKey]) : null;
        if (key === this.selectionKey) return;
        this.selectionKey = key;
        this.pageIndex = 1;
        this.isExpanded = false;
        this.load();
    }

    ngOnDestroy(): void {
        this.cancelRequests();
        this.sessionSubscription.unsubscribe();
        this.invalidationSubscription.unsubscribe();
    }

    @HostListener("window:focus")
    protected refreshDueColorsOnFocus(): void {
        if (this.isVisible) this.cdr.markForCheck();
    }

    @HostListener("click")
    protected expandIfCollapsed(): void {
        if (!this.isExpanded) {
            this.isExpanded = true;
            this.cdr.markForCheck();
        }
    }

    protected toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
        this.cdr.markForCheck();
    }

    protected trackWorkItem(_index: number, item: AssessmentWorkItem): string {
        return workItemIdentity(item);
    }

    protected assigneeSummary(item: AssessmentWorkItem): string {
        const assignees = item.kind === "task" ? item.task.assignees : item.evidence.assignees;
        if (!assignees.length) return "Unassigned";
        const names = assignees.slice(0, 2).map((assignee) => assignee.displayName);
        const remaining = assignees.length - names.length;
        return remaining ? `${names.join(", ")} +${remaining}` : names.join(", ");
    }

    protected openItem(item: AssessmentWorkItem): void {
        const licenseId = this.productLicenseId;
        if (!this.isVisible || this.isLoading || !positiveId(licenseId)) return;
        const identity = workItemIdentity(item);
        const current = this.workItems.find((row) => workItemIdentity(row) === identity);
        if (!current) return;
        if (current.kind === "task") this.taskBrowser.openTask(licenseId, current.task.id);
        else this.evidenceLaunch.openItem(licenseId, current.evidence.id);
    }

    protected openAll(): void {
        const requirement = this.requirement;
        if (!this.isVisible || this.isLoading || !positiveId(this.productLicenseId)) return;
        if (this.scope === "assessment") this.taskBrowser.openAssessment(this.productLicenseId);
        else if (requirement) this.taskBrowser.openRequirement(this.productLicenseId, requirement);
    }

    protected openRequiredEvidence(): void {
        if (this.scope !== "assessment" || !this.isVisible || this.isLoading ||
            !positiveId(this.productLicenseId) || !this.sessionToken ||
            this.sessionToken !== this.login.getToken()) return;
        this.evidenceLaunch.openAssessment(this.productLicenseId);
    }

    protected addTask(): void {
        const requirement = this.requirement;
        if (!this.isVisible || !this.canAddTask || this.isCheckingCreation || !positiveId(this.productLicenseId)) return;
        if (this.scope === "assessment") this.taskForm.openAssessmentCreate(this.productLicenseId);
        else if (requirement) this.taskForm.openRequirementCreate(this.productLicenseId, requirement);
    }

    protected retry(): void {
        this.load();
    }

    protected onPageChange(page: number): void {
        if (!positiveId(page) || page === this.pageIndex) return;
        this.pageIndex = page;
        this.load();
    }

    private shouldRefreshForInvalidation(event: AssessmentTaskInvalidation): boolean {
        const requirement = this.requirement;
        if (!this.sessionIdentity || !this.sessionToken ||
            this.sessionToken !== this.login.getToken() || !positiveId(this.productLicenseId) ||
            (this.scope !== "assessment" && !requirement) ||
            (event.productLicenseId !== null && event.productLicenseId !== this.productLicenseId)) return false;
        if (this.scope === "assessment") return true;
        return event.affectedRequirements === null || event.affectedRequirements.some((item) =>
            item.sectionKey === requirement.sectionKey && item.requirementKey === requirement.requirementKey);
    }

    private shouldRefreshForEvidenceInvalidation(change: EvidenceRequirementInvalidation): boolean {
        const licenseId = this.productLicenseId;
        const requirement = this.requirement;
        if (!this.sessionIdentity || !this.sessionToken ||
            this.sessionToken !== this.login.getToken() || !this.selectionKey || !positiveId(licenseId) ||
            (this.scope !== "assessment" && !requirement)) return false;
        return evidenceChangeAffectsFooter(
            change,
            this.login.getCurrentAccountId(),
            licenseId,
            this.scope === "assessment" ? null : requirement
        );
    }

    private get requirement(): AssessmentTaskRequirementRef | null {
        if (!validFooterRequirementKey(this.sectionKey) ||
            !validFooterRequirementKey(this.requirementKey)) return null;
        return { sectionKey: this.sectionKey, requirementKey: this.requirementKey };
    }

    private load(): void {
        this.cancelRequests();
        this.workItems = [];
        this.total = 0;
        this.isVisible = false;
        this.canAddTask = false;
        this.isCheckingCreation = false;
        this.loadError = null;
        this.creationError = null;

        const requirement = this.requirement;
        const licenseId = this.productLicenseId;
        const filters = positiveId(licenseId)
            ? footerWorkItemFilters(this.scope, licenseId, requirement, this.pageIndex) : null;
        if (!this.sessionIdentity || !this.sessionToken || !this.login.isLoggedIn() || !positiveId(licenseId) ||
            !filters) {
            this.isLoading = false;
            this.cdr.markForCheck();
            return;
        }

        const version = this.requestVersion;
        const pageIndex = this.pageIndex;
        const selectionKey = this.selectionKey;
        const sessionIdentity = this.sessionIdentity;
        const token = this.login.getToken();
        const isCurrent = (): boolean => version === this.requestVersion &&
            selectionKey === this.selectionKey && sessionIdentity === this.sessionIdentity &&
            token === this.sessionToken && token === this.login.getToken();
        this.isLoading = true;
        this.cdr.markForCheck();
        this.pageRequest = this.licenses.loadLicenses().pipe(
            switchMap((licenses) => {
                const license = licenses.find((item) => item.id === licenseId) ?? null;
                if (!isCurrent() || !license || this.scope === "requirement" &&
                    !canShowTaskAssignmentsFooter(
                        license, licenseId, this.login.getCurrentUserId(), this.login.hasAdminPrivileges()
                    )) return of({ license, page: null as AssessmentWorkItemPage | null });
                return this.workItemService.listWorkItems(filters).pipe(map((page) => ({ license, page })));
            })
        ).subscribe({
            next: ({ license, page }) => {
                if (!isCurrent()) return;
                if (!license) {
                    this.showLoadError("Assignments are unavailable.");
                    return;
                }
                if (!page) {
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    return;
                }
                if (page.page !== pageIndex || page.pageSize !== FOOTER_PAGE_SIZE) {
                    this.showLoadError("Assignments are unavailable.");
                    return;
                }
                const lastPage = Math.max(1, Math.ceil(page.total / FOOTER_PAGE_SIZE));
                if (pageIndex > lastPage) {
                    this.pageIndex = lastPage;
                    this.load();
                    return;
                }
                this.workItems = scopeFooterWorkItems(
                    page.items, licenseId, this.scope === "assessment" ? null : requirement);
                this.total = page.total;
                this.finishLoading(licenseId, license, requirement, version);
            },
            error: () => {
                if (isCurrent()) this.showLoadError("Unable to load assignments.");
            }
        });
    }

    private finishLoading(
        licenseId: number,
        license: ProductLicenseSummary,
        requirement: AssessmentTaskRequirementRef | null,
        version: number
    ): void {
        if (version !== this.requestVersion || license.id !== licenseId) return;
        this.isVisible = true;
        this.isLoading = false;
        if (this.scope === "assessment") {
            this.isCheckingCreation = true;
            this.cdr.markForCheck();
            this.creationRequest = this.taskService.getCreationOptions(licenseId, { assigneeLimit: 1, requirementLimit: 1 }).subscribe({
                next: (options) => {
                    if (version !== this.requestVersion) return;
                    this.canAddTask = options.canCreate;
                    this.isCheckingCreation = false;
                    this.cdr.markForCheck();
                },
                error: (error: unknown) => {
                    if (version !== this.requestVersion) return;
                    this.isCheckingCreation = false;
                    if (!(error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404))) {
                        this.creationError = "Unable to check task creation access.";
                    }
                    this.cdr.markForCheck();
                }
            });
            return;
        }
        if (!requirement) return;
        this.isCheckingCreation = true;
        this.cdr.markForCheck();
        this.loadCreationEligibility(licenseId, requirement, version, 0);
    }

    private loadCreationEligibility(
        licenseId: number,
        requirement: AssessmentTaskRequirementRef,
        version: number,
        offset: number
    ): void {
        const search = Array.from(requirement.requirementKey).slice(0, 50).join("");
        this.creationRequest = this.taskService.getCreationOptions(licenseId, {
            requirementOffset: offset,
            requirementLimit: CREATION_OPTION_PAGE_SIZE,
            requirementSearch: search,
            assigneeLimit: 1
        }).subscribe({
            next: (options) => {
                if (version !== this.requestVersion) return;
                if (options.requirements.items.some((item) =>
                    item.sectionKey === requirement.sectionKey &&
                    item.requirementKey === requirement.requirementKey)) {
                    this.canAddTask = options.canCreate;
                } else if (options.requirements.items.length > 0 &&
                    offset + options.requirements.items.length < options.requirements.total) {
                    this.loadCreationEligibility(
                        licenseId, requirement, version, offset + options.requirements.items.length
                    );
                    return;
                }
                this.isCheckingCreation = false;
                this.cdr.markForCheck();
            },
            error: (error: unknown) => {
                if (version !== this.requestVersion) return;
                this.isCheckingCreation = false;
                if (!(error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404))) {
                    this.creationError = "Unable to check task creation access.";
                }
                this.cdr.markForCheck();
            }
        });
    }

    private showLoadError(message: string): void {
        this.workItems = [];
        this.total = 0;
        this.isVisible = false;
        this.isLoading = false;
        this.loadError = message;
        this.cdr.markForCheck();
    }

    private cancelRequests(): void {
        this.requestVersion += 1;
        this.pageRequest?.unsubscribe();
        this.creationRequest?.unsubscribe();
        this.pageRequest = null;
        this.creationRequest = null;
    }
}
